import { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/features/auth/hooks/useAuth';

/**
 * Keeps a guide-lifecycle user on the one lifecycle screen that matches their
 * state, by asking the shared resolver rather than deciding for itself.
 *
 * Replaces `GuideApplicationGate` and `OnboardingEntryGuard`, which each made
 * their own redirect decision from different inputs.
 *
 * Scope is deliberately limited to the lifecycle screens below. An applicant
 * is a traveler with an application attached — `role` stays `traveler` until
 * approval — so they keep the traveler app, including bookings they have
 * already paid for. Sending them to their status screen at sign-in is the job
 * of `postAuthRoute`, not of a gate that outlives the sign-in.
 */

/** The screens this gate arbitrates between. Nothing else is gated. */
const LIFECYCLE_PATHS = [
  '/guide-application',
  '/guide-profile',
  '/onboarding',
  '/submission-success',
];

function isLifecyclePath(pathname: string): boolean {
  return LIFECYCLE_PATHS.some((path) => pathname.startsWith(path));
}

export function GuideLifecycleGate({ children }: { children: ReactNode }) {
  const { isInitializing, isAuthenticated, guideLifecycleRoute } = useAuth();
  const location = useLocation();

  if (isInitializing || !isAuthenticated) {
    return <>{children}</>;
  }

  // No application on file — ordinary traveler, nothing to arbitrate.
  if (!guideLifecycleRoute) {
    return <>{children}</>;
  }

  // Outside the lifecycle screens the resolver has no opinion worth enforcing.
  if (!isLifecyclePath(location.pathname)) {
    return <>{children}</>;
  }

  if (location.pathname !== guideLifecycleRoute) {
    return (
      <Navigate to={guideLifecycleRoute} replace state={{ from: location.pathname }} />
    );
  }

  return <>{children}</>;
}
